import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, Nfc, Loader2, CheckCircle2, XCircle, ShieldOff } from "lucide-react";
import { api, errMsg, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageView, ExchangeModal } from "../components/PageView";
import { Btn, Input, Select } from "../components/kit";
import { Logo } from "../components/AppShell";

export const Notice = ({ status = "not_found", message }) => {
  const Icon = status === "disabled" ? ShieldOff : status === "inactive" ? XCircle : XCircle;
  return (
    <div className="app-light grid place-items-center p-6 text-center" data-testid={`notice-${status}`}>
      <div className="max-w-sm rise">
        <Icon className="h-10 w-10 mx-auto text-zinc-500 mb-4" />
        <h1 className="text-2xl font-bold">{status === "inactive" ? "Currently inactive" : status === "disabled" ? "Unavailable" : "Not found"}</h1>
        <p className="text-zinc-400 mt-2 text-sm" data-testid="notice-message">{message || "We couldn't find what you were looking for."}</p>
        <Link to="/" className="inline-block mt-8"><Logo className="justify-center opacity-70" /></Link>
      </div>
    </div>
  );
};

const Spinner = () => <div className="min-h-screen grid place-items-center bg-[#141414]"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>;

function useSeo(page) {
  useEffect(() => {
    if (!page) return;
    document.title = page.seo?.title || page.title;
    const set = (sel, attr, val) => { let m = document.querySelector(sel); if (!m) { m = document.createElement("meta"); m.setAttribute(attr[0], attr[1]); document.head.appendChild(m); } m.setAttribute("content", val || ""); };
    set('meta[name="description"]', ["name", "description"], page.seo?.description);
    set('meta[property="og:title"]', ["property", "og:title"], page.seo?.title || page.title);
    if (page.seo?.og_image) set('meta[property="og:image"]', ["property", "og:image"], fileUrl(page.seo.og_image));
    if (page.favicon) { const l = document.querySelector("link[rel='icon']") || document.createElement("link"); l.rel = "icon"; l.href = fileUrl(page.favicon); document.head.appendChild(l); }
  }, [page]);
}

export function PublicPage() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [res, setRes] = useState(null);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [ex, setEx] = useState(false);
  useEffect(() => { api.get(`/public/resolve/${slug}`, { headers: { "X-Page-Referrer": document.referrer } }).then((r) => setRes(r.data)).catch(() => setRes({ status: "not_found" })); }, [slug]);
  useSeo(res?.page);
  if (!res) return <Spinner />;
  if (res.status === "protected") {
    const unlock = async (e) => { e.preventDefault(); try { setRes((await api.post(`/public/resolve/${slug}`, { password: pw })).data); } catch (er) { setErr(errMsg(er)); } };
    return (
      <form onSubmit={unlock} className="app-light grid place-items-center p-6">
        <div className="w-full max-w-xs text-center space-y-4 rise">
          <Lock className="h-8 w-8 mx-auto text-zinc-500" /><h1 className="text-xl font-bold">{res.title}</h1><p className="text-sm text-zinc-400">This page is password protected.</p>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" data-testid="page-password-input" />
          {err && <p className="text-sm text-red-400">{err}</p>}
          <Btn className="w-full" data-testid="page-password-submit">Unlock</Btn>
        </div>
      </form>
    );
  }
  if (res.status !== "ok") return <Notice {...res} />;
  const src = sp.get("src") === "card" ? { type: "card" } : sp.get("utm_source") || sp.get("src") === "qr" ? { type: "qr" } : null;
  return (
    <>
      <PageView page={res.page} org={res.org} removeBranding={res.remove_branding} onExchange={() => setEx(true)} />
      {ex && <ExchangeModal page={res.page} org={res.org} src={src} onClose={() => setEx(false)} />}
    </>
  );
}

export function QRResolve() {
  const { code } = useParams();
  const [res, setRes] = useState(null);
  useEffect(() => {
    api.get(`/public/q/${code}${window.location.search}`).then((r) => { if (r.data.status === "redirect") window.location.replace(r.data.url); else setRes(r.data); }).catch(() => setRes({ status: "not_found" }));
  }, [code]);
  return res ? <Notice {...res} /> : <Spinner />;
}

export function CardResolve() {
  const { serial } = useParams();
  const { me } = useAuth();
  const nav = useNavigate();
  const [res, setRes] = useState(null);
  const [pages, setPages] = useState([]);
  const [pageId, setPageId] = useState("");
  const [state, setState] = useState({});
  useEffect(() => {
    api.get(`/public/c/${serial}`).then((r) => { if (r.data.status === "redirect") nav(`/${r.data.slug}?src=card`, { replace: true }); else setRes(r.data); }).catch(() => setRes({ status: "not_found" }));
  }, [serial, nav]);
  useEffect(() => {
    if (me && !me.needs_onboarding && res?.status === "unactivated") api.get("/pages").then((r) => { const ps = [...r.data].sort((a, b) => (b.page_kind === "bio") - (a.page_kind === "bio")); setPages(ps); setPageId(ps[0]?.id || ""); });
  }, [me, res]);
  if (!res) return <Spinner />;
  if (res.status !== "unactivated") return <Notice {...res} />;
  const activate = async () => {
    setState({ busy: true });
    try { await api.post(`/cards/${serial}/activate`, { page_id: pageId }); setState({ done: true }); } catch (e) { setState({ err: errMsg(e) }); }
  };
  return (
    <div className="app-light grid place-items-center p-6">
      <div className="w-full max-w-sm text-center rise" data-testid="activate-card-screen">
        <div className="h-16 w-16 rounded-2xl bg-blue-600/15 grid place-items-center mx-auto mb-5"><Nfc className="h-8 w-8 text-blue-400" /></div>
        <h1 className="text-2xl font-bold">Activate this card</h1>
        <p className="text-zinc-400 text-sm mt-2">Card <span className="font-mono text-zinc-200">{serial.toUpperCase()}</span> isn't linked yet. Link it to your digital business card and every tap will open it.</p>
        {state.done ? (
          <div className="mt-8" data-testid="activate-success"><CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto" /><p className="mt-2 font-semibold">Card activated!</p><Btn className="mt-5" onClick={() => window.location.reload()}>Tap to test</Btn></div>
        ) : !me || me.needs_onboarding ? (
          <Link to={`/login?next=/c/${serial}`}><Btn className="w-full mt-8" data-testid="activate-login-btn">Log in or sign up to activate</Btn></Link>
        ) : pages.length === 0 ? (
          <div className="mt-8 space-y-3"><p className="text-sm text-zinc-400">You need a page first — a Bio page works best.</p><Link to="/dashboard/pages"><Btn className="w-full" data-testid="activate-create-card-btn">Create a digital card</Btn></Link></div>
        ) : (
          <div className="mt-8 space-y-3 text-left">
            <Select value={pageId} onChange={(e) => setPageId(e.target.value)} options={pages.map((p) => [p.id, `${p.title} (/${p.slug})`])} data-testid="activate-page-select" />
            {state.err && <p className="text-sm text-red-400" data-testid="activate-error">{state.err}</p>}
            <Btn className="w-full" loading={state.busy} onClick={activate} data-testid="activate-card-btn">Activate card</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

export function PaymentResult({ cancelled }) {
  const [sp] = useSearchParams();
  const [st, setSt] = useState(cancelled ? { payment_status: "cancelled" } : null);
  useEffect(() => {
    if (cancelled) return;
    let n = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sp.get("session_id")}`);
        setSt(data);
        if (data.payment_status !== "paid" && data.status !== "expired" && n++ < 15) setTimeout(poll, 2000);
      } catch { setSt({ payment_status: "error" }); }
    };
    poll();
  }, [cancelled, sp]);
  const paid = st?.payment_status === "paid";
  const back = st?.kind === "card_order" ? `/dashboard/cards/orders/${st.order_id}` : "/dashboard/settings/billing";
  return (
    <div className="app-light grid place-items-center p-6 text-center">
      <div className="max-w-sm rise" data-testid="payment-result">
        {!st || (!paid && st.payment_status === "pending") ? <Loader2 className="h-8 w-8 animate-spin mx-auto text-zinc-500" /> : paid ? <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-400" /> : <XCircle className="h-10 w-10 mx-auto text-zinc-500" />}
        <h1 className="text-2xl font-bold mt-4" data-testid="payment-status-text">{paid ? "Payment successful" : cancelled ? "Payment cancelled" : !st || st.payment_status === "pending" ? "Confirming payment…" : "Payment not completed"}</h1>
        <Link to={paid ? back : "/dashboard"}><Btn className="mt-8" data-testid="payment-continue-btn">Continue</Btn></Link>
      </div>
    </div>
  );
}
